import { useId, useState, type ReactNode } from 'react';
import { ArrowUturnLeftIcon, BellSnoozeIcon, CheckCircleIcon } from '@heroicons/react/24/outline';
import type { VirtualAppointmentDto } from '../../services/virtualAppointments';
import { VirtualAppointmentAccordionItem } from './VirtualAppointmentAccordionItem';
import { VirtualAppointmentReadOnlyDescription } from './VirtualAppointmentDetailPanels';
import { VirtualAppointmentRowBadges } from './VirtualAppointmentRowBadges';
import { FieldLabel } from './VirtualAppointmentFieldParts';
import { VirtualAppointmentSnoozeModal } from './VirtualAppointmentSnoozeModal';
import { VirtualAppointmentStatusDropdown } from './VirtualAppointmentStatusDropdown';
import { VirtualAppointmentTeamDropdown } from './VirtualAppointmentTeamDropdown';
import { isOpenStatus } from './openVirtualAppointments';
import {
    useVirtualAppointmentRowActions,
    type VirtualAppointmentRowActions
} from './useVirtualAppointmentRowActions';
import { formatClosedLabel, summaryFromDto } from './virtualAppointmentSummary';
import { MARK_DONE_BUTTON } from './virtualAppointmentActionStyles';

// Same look as the conversations bar's Mark as read / Mark as unread pair.
const MARK_UNDONE_BUTTON =
    'inline-flex items-center gap-1.5 text-xs font-medium text-orange-600 bg-orange-50 hover:bg-orange-100 border border-orange-200 rounded-lg px-3 py-1.5 transition-colors disabled:opacity-50 disabled:cursor-not-allowed';
const SNOOZE_BUTTON =
    'inline-flex items-center gap-1.5 text-xs font-semibold text-gray-700 bg-white hover:bg-gray-50 border border-gray-300 rounded-lg px-3 py-1.5 transition-colors disabled:opacity-50 disabled:cursor-not-allowed';

function Spinner({ tone }: { tone: 'green' | 'orange' }) {
    const border = tone === 'green' ? 'border-green-400' : 'border-orange-400';
    return (
        <span
            className={`h-3.5 w-3.5 rounded-full border-2 ${border} border-t-transparent animate-spin`}
        />
    );
}

interface ControlFieldProps {
    label: string;
    htmlFor: string;
    children: ReactNode;
}

// Equal columns while both labels fit side by side, each never narrower than its full label; a
// field that can't share the row wraps onto its own.
function ControlField({ label, htmlFor, children }: ControlFieldProps) {
    return (
        <div className="flex flex-col flex-1 basis-0 min-w-max">
            <FieldLabel htmlFor={htmlFor}>{label}</FieldLabel>
            {children}
        </div>
    );
}

function ControlError({ message }: { message: string | null }) {
    if (!message) return null;
    return (
        <p role="alert" className="text-xs text-red-500">
            {message}
        </p>
    );
}

interface ControlsProps {
    va: VirtualAppointmentDto;
    actions: VirtualAppointmentRowActions;
}

function OpenControls({ va, actions, onSnooze }: ControlsProps & { onSnooze: () => void }) {
    const statusId = useId();
    const teamId = useId();
    if (!isOpenStatus(va.status)) return null;
    return (
        <div className="flex flex-col gap-2">
            <div className="flex flex-wrap gap-x-3 gap-y-2">
                <ControlField label="Status" htmlFor={statusId}>
                    <VirtualAppointmentStatusDropdown
                        id={statusId}
                        value={va.status}
                        onChange={actions.setStatus}
                        disabled={actions.pending}
                    />
                </ControlField>
                <ControlField label="Team" htmlFor={teamId}>
                    <VirtualAppointmentTeamDropdown
                        id={teamId}
                        value={va.team}
                        onChange={actions.setTeam}
                        disabled={actions.pending}
                    />
                </ControlField>
            </div>
            <div className="grid grid-cols-2 gap-3">
                <button
                    type="button"
                    onClick={() => actions.setStatus('done')}
                    disabled={actions.pending}
                    title="Mark as done"
                    className={`${MARK_DONE_BUTTON} justify-center`}
                >
                    {actions.statusPending ? (
                        <Spinner tone="green" />
                    ) : (
                        <CheckCircleIcon className="w-3.5 h-3.5" />
                    )}
                    Done
                </button>
                <button
                    type="button"
                    onClick={onSnooze}
                    disabled={actions.pending}
                    className={`${SNOOZE_BUTTON} justify-center`}
                >
                    <BellSnoozeIcon className="w-3.5 h-3.5" />
                    Snooze
                </button>
            </div>
        </div>
    );
}

function DoneControls({ actions }: Pick<ControlsProps, 'actions'>) {
    return (
        <div className="flex justify-end">
            <button
                type="button"
                onClick={() => actions.setStatus('open')}
                disabled={actions.pending}
                className={MARK_UNDONE_BUTTON}
            >
                {actions.statusPending ? (
                    <Spinner tone="orange" />
                ) : (
                    <ArrowUturnLeftIcon className="w-3.5 h-3.5" />
                )}
                Mark as undone
            </button>
        </div>
    );
}

interface RoundVirtualAppointmentItemProps {
    patientId: string;
    va: VirtualAppointmentDto;
    currentAligner: number | null;
    totalAligners: number | null;
    treatmentStartDate: string | null;
    dmLinked: boolean;
    expanded: boolean;
    onToggle: () => void;
}

// One card per VA of the round, open or done, so marking one done leaves it in place (folded).
export function RoundVirtualAppointmentItem({
    patientId,
    va,
    currentAligner,
    totalAligners,
    treatmentStartDate,
    dmLinked,
    expanded,
    onToggle
}: RoundVirtualAppointmentItemProps) {
    const summary = summaryFromDto(va, treatmentStartDate);
    const actions = useVirtualAppointmentRowActions(patientId, va.id);
    const [snoozing, setSnoozing] = useState(false);
    const done = va.status === 'done';
    const closeSnooze = () => {
        setSnoozing(false);
        actions.resetSnoozeError();
    };

    return (
        <>
            <VirtualAppointmentAccordionItem
                summary={summary}
                expanded={expanded}
                onToggle={onToggle}
                done={done}
                meta={formatClosedLabel(va)}
                team={va.team}
                foldedBadges={<VirtualAppointmentRowBadges va={va} />}
            >
                <VirtualAppointmentReadOnlyDescription doc={va.descriptionDoc} layout="plain" />
                <div className="border-t border-gray-100" />
                {done ? (
                    <DoneControls actions={actions} />
                ) : (
                    <OpenControls va={va} actions={actions} onSnooze={() => setSnoozing(true)} />
                )}
                <ControlError message={actions.statusError} />
                <ControlError message={actions.teamError} />
            </VirtualAppointmentAccordionItem>
            {snoozing && (
                <VirtualAppointmentSnoozeModal
                    typeTitle={summary.typeTitle}
                    currentAligner={currentAligner}
                    totalAligners={totalAligners}
                    treatmentStartDate={treatmentStartDate}
                    dmLinked={dmLinked}
                    currentNogos={va.triggerKind === 'nogos' ? va.triggerNogos : null}
                    submitting={actions.pending}
                    apiError={actions.snoozeError}
                    onSubmit={(trigger) => actions.snooze(trigger, closeSnooze)}
                    onClose={closeSnooze}
                />
            )}
        </>
    );
}
